import { camelCase, upperFirst } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import bookingDocumentsAPI from '../../../services/api/booking-documents';
import { formatDate } from '../../../shared/helpers/formatDate';
import useStore from '../../../store';
import {
  BookingWithDocuments,
  BookingDocument,
} from '../../../types/documents';
import { Collapsible } from '../../UI/Collapsible';
import {
  StyledCollapsibleContent,
  StyledCollapsibleControl,
  StyledCollapsibleControlArrow,
  StyledCollapsibleControlText,
  StyledDescriptionList,
  StyledDocumentDownloadButton,
  StyledDocumentDownloadIcon,
  StyledDocumentsList,
  StyledDocumentsListItem,
  StyledDocumentsListItemDate,
  StyledListItem,
  StyledSpinner,
  StyledTitle,
} from './DocumentListItem.styles';

export type DocumentListItemProps = BookingWithDocuments;

const DOWNLOAD_DOC_WAIT_MILLIS = 5000;

export function DocumentListItem({
  id,
  property_address,
  status,
  documents,
}: DocumentListItemProps): JSX.Element {
  const { t } = useTranslation(['booking']);
  const { user } = useStore(state => ({
    user: state.user,
  }));
  const [isDownloadDisabled, setIsDownloadDisabled] = useState<boolean>(false);

  async function handleDownloadClick(documentId: BookingDocument['id']) {
    setIsDownloadDisabled(true);
    setTimeout(() => setIsDownloadDisabled(false), DOWNLOAD_DOC_WAIT_MILLIS);

    const preSignedUrl = await bookingDocumentsAPI.getBookingDocumentURL({
      bookingId: id,
      bookingAttachmentId: documentId,
    });

    window.open(preSignedUrl);
  }

  return (
    <StyledListItem>
      <div>
        <StyledTitle>{property_address}</StyledTitle>
        <StyledDescriptionList user={user?.type}>
          <dt>{t('booking:status')}</dt>
          <dd>{upperFirst(t(`booking:${status}`))}</dd>
        </StyledDescriptionList>
      </div>
      <Collapsible allowMultiple>
        <StyledCollapsibleControl bookingid={id as string}>
          <StyledCollapsibleControlText>
            {documents.length}{' '}
            {documents.length === 1
              ? t('booking:document')
              : t('booking:documents')}
          </StyledCollapsibleControlText>
          <StyledCollapsibleControlArrow />
        </StyledCollapsibleControl>
        <StyledCollapsibleContent bookingid={id as string}>
          <StyledDocumentsList>
            {documents.map(doc => (
              <StyledDocumentsListItem key={doc.id}>
                <StyledDocumentsListItemDate>
                  {formatDate(new Date(doc.created_at), {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </StyledDocumentsListItemDate>
                {upperFirst(t(`booking:${camelCase(doc.type)}`))}
                <StyledDocumentDownloadButton
                  user={user?.type}
                  variant="primary"
                  onClick={handleDownloadClick.bind(null, doc.id)}
                  disabled={isDownloadDisabled}
                >
                  {isDownloadDisabled ? (
                    <StyledSpinner />
                  ) : (
                    <StyledDocumentDownloadIcon aria-hidden="true" />
                  )}
                  {upperFirst(t('booking:downloadDocument'))}
                </StyledDocumentDownloadButton>
              </StyledDocumentsListItem>
            ))}
          </StyledDocumentsList>
        </StyledCollapsibleContent>
      </Collapsible>
    </StyledListItem>
  );
}
